import React, { useMemo } from 'react';
import * as THREE from 'three';
import { Billboard, Text } from '@react-three/drei';
import type { LayoutElement, TrackNodeAnnotation } from '../../types';
import { TRACK_3D_ELEVATION } from '../../utils/layout3d';
import { resolveDerivedGateModules } from '../../utils/derivedGateEquipment';
import DerivedGateEquipment3D from './DerivedGateEquipment3D';

type EndpointRole = 'start' | 'end';

type EndpointFlag = {
  role: EndpointRole;
  x: number;
  y: number;
};

const FLAG_COLORS: Record<EndpointRole, { cloth: string; edge: string }> = {
  start: { cloth: '#22c55e', edge: '#15803d' },
  end: { cloth: '#ef4444', edge: '#b91c1c' },
};

const Flag3D: React.FC<{ marker: EndpointFlag }> = ({ marker }) => {
  const colors = FLAG_COLORS[marker.role];
  const shape = useMemo(() => {
    const flag = new THREE.Shape();
    flag.moveTo(0, 0);
    flag.lineTo(5.5, -1.8);
    flag.lineTo(0, -3.6);
    flag.closePath();
    return flag;
  }, []);

  return (
    <group position={[marker.x, TRACK_3D_ELEVATION + 1, marker.y]}>
      <mesh position={[0, 5, 0]}>
        <cylinderGeometry args={[0.22, 0.28, 10, 10]} />
        <meshStandardMaterial color="#475569" metalness={0.72} roughness={0.3} />
      </mesh>

      {/* Two crossed faces keep the small flag visible from every camera angle. */}
      {[0, Math.PI / 2].map((rotationY) => (
        <mesh
          key={rotationY}
          position={[0, 9.2, 0]}
          rotation={[0, rotationY, 0]}
          renderOrder={20}
        >
          <shapeGeometry args={[shape]} />
          <meshStandardMaterial
            color={colors.cloth}
            emissive={colors.edge}
            emissiveIntensity={0.12}
            side={THREE.DoubleSide}
            metalness={0.08}
            roughness={0.58}
            depthTest
          />
        </mesh>
      ))}

      <mesh position={[0, 0.15, 0]}>
        <cylinderGeometry args={[0.65, 0.78, 0.3, 12]} />
        <meshStandardMaterial color={colors.edge} metalness={0.35} roughness={0.42} />
      </mesh>

      <Billboard position={[2.4, 11.2, 0]}>
        <Text
          fontSize={1.6}
          color={colors.cloth}
          outlineColor="#ffffff"
          outlineWidth={0.08}
          anchorX="center"
          anchorY="middle"
        >
          {marker.role === 'start' ? 'START' : 'END'}
        </Text>
      </Billboard>
    </group>
  );
};

/**
 * Persistent route START/END landmarks in the 3D scene.
 * Entry/Exit track-node annotations are authoritative; legacy endpoint elements
 * are retained as a fallback for routes loaded before annotations were added.
 */
const RouteEndpointFlags3D: React.FC<{
  elements: Record<string, LayoutElement>;
  annotations: TrackNodeAnnotation[];
}> = ({ elements, annotations }) => {
  const modules = useMemo(
    () => resolveDerivedGateModules(Object.values(elements), annotations),
    [annotations, elements]
  );
  const flags = useMemo(() => {
    const byRole: Partial<Record<EndpointRole, EndpointFlag>> = {};
    const moduleRoles = new Set(
      modules.map((module) => (module.role === 'entry' ? 'start' : 'end'))
    );

    for (const element of Object.values(elements)) {
      const role = element.customProperties?.endpointRole;
      if (
        (role !== 'start' && role !== 'end') ||
        byRole[role] ||
        moduleRoles.has(role)
      ) {
        continue;
      }
      byRole[role] = {
        role,
        x: element.position.x + (element.dimensions.width ?? 0) / 2,
        y: element.position.y + (element.dimensions.height ?? 0) / 2,
      };
    }

    return (['start', 'end'] as const)
      .map((role) => byRole[role])
      .filter((flag): flag is EndpointFlag => Boolean(flag));
  }, [elements, modules]);

  return (
    <>
      {modules.map((module) => (
        <DerivedGateEquipment3D key={module.loadPortId} module={module} />
      ))}
      {flags.map((marker) => (
        <Flag3D key={marker.role} marker={marker} />
      ))}
    </>
  );
};

export default React.memo(RouteEndpointFlags3D);
